import React, { useEffect, useRef, useState } from 'react';
import { ENRICH_STEPS } from '../../lib/steps';

// Shared spend gate for "Enrich my data", used by the Pipeline hero and the Quality-tab
// card so both route through the SAME confirm dialog and caps. Enrichment spends money;
// the click on "Enrich" in this dialog IS the consent.
export const TEAL = '#00C6AE';

// A user-run enriches a bounded batch with the AI providers. The caps carry over: the
// rest of the backlog waits for the next run. Stated plainly in the dialog.
export const ENRICH_DEFAULTS = { AI_PROVIDER: 'openai', COMPANY_CAP: 500, PEOPLE_CAP: 2000 };

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

// useEnrichAction owns the confirm/steps state so a card only has to render the button
// and the dialog. runEnrich omits `steps` when all are selected (API treats absent as all).
export function useEnrichAction(onRunEnrichment) {
  const [confirming, setConfirming] = useState(false);
  const [selectedSteps, setSelectedSteps] = useState(ENRICH_STEPS.map((s) => s.name));
  const toggleStep = (name) =>
    setSelectedSteps((prev) => (prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]));
  const runEnrich = () => {
    setConfirming(false);
    const steps = selectedSteps.length === ENRICH_STEPS.length ? undefined : selectedSteps;
    onRunEnrichment({
      provider: ENRICH_DEFAULTS.AI_PROVIDER,
      companyCap: String(ENRICH_DEFAULTS.COMPANY_CAP),
      peopleCap: String(ENRICH_DEFAULTS.PEOPLE_CAP),
      steps
    });
  };
  return { confirming, setConfirming, selectedSteps, toggleStep, runEnrich };
}

// ConfirmDialog — Escape and backdrop close it; the primary button is focused on open so
// a keyboard user can confirm or cancel without hunting.
export default function ConfirmDialog({ open, onCancel, onConfirm, companies, people, busy, selectedSteps, onToggleStep }) {
  const confirmRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    confirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onCancel} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="enrich-confirm-title"
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="enrich-confirm-title" className="text-lg font-semibold text-gray-900">Enrich your data with AI?</h3>
        <p className="text-sm text-gray-600 mt-2">
          SignalCue will use AI to fill gaps in <strong className="text-gray-900">up to {fmt(companies)} companies</strong> and{' '}
          <strong className="text-gray-900">{fmt(people)} people</strong> in this run — websites, industries, locations and
          LinkedIn pages. Anything beyond that carries over to your next run.
        </p>
        <p className="text-xs text-gray-500 mt-3">
          This uses external AI services and may take a few minutes. You&apos;ll see progress as it runs, and you can pause it
          any time.
        </p>
        <fieldset className="mt-4 border-t border-gray-100 pt-4">
          <legend className="text-xs font-semibold text-gray-700">Steps to run</legend>
          <p className="text-[11px] text-gray-400 mt-0.5">
            All on by default. They run in order — turning off an early step (country, website) can lower later fills.
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
            {ENRICH_STEPS.map((s) => (
              <label key={s.name} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={selectedSteps.includes(s.name)}
                  onChange={() => onToggleStep(s.name)}
                  className="rounded border-gray-300"
                  style={{ accentColor: TEAL }}
                />
                {s.label}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy || selectedSteps.length === 0}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ background: TEAL, '--tw-ring-color': TEAL }}
          >
            {busy ? 'Starting…' : 'Enrich my data'}
          </button>
        </div>
      </div>
    </div>
  );
}
